import React, { useEffect, useState } from "react";
import { Pencil, Plus, Trash2, Save, X, Feather } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const NoteCard = ({ note, onEdit, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(note.content);
  const [signedName, setSignedName] = useState(note.signed_name || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await onEdit(note.id, { content, signed_name: signedName });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="rounded-xl border border-sky-700/40 bg-gradient-to-br from-sky-950/40 to-[#0F0407] p-6 backdrop-blur-sm">
        <Textarea
          data-testid={`her-note-edit-content-${note.id}`}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={5}
          className="bg-[#0F0407]/80 border-sky-800/60 text-sky-50"
        />
        <Input
          data-testid={`her-note-edit-signed-${note.id}`}
          value={signedName}
          onChange={(e) => setSignedName(e.target.value)}
          placeholder="Signé... (optionnel)"
          className="bg-[#0F0407]/80 border-sky-800/60 text-sky-50 mt-3"
        />
        <div className="flex gap-2 mt-4 justify-end">
          <Button
            data-testid={`her-note-cancel-${note.id}`}
            variant="ghost"
            onClick={() => {
              setEditing(false);
              setContent(note.content);
              setSignedName(note.signed_name || "");
            }}
            className="text-sky-200 hover:bg-sky-900/30 rounded-full"
          >
            <X className="w-4 h-4 mr-1" /> Annuler
          </Button>
          <Button
            data-testid={`her-note-save-${note.id}`}
            onClick={save}
            disabled={saving}
            className="bg-sky-600 hover:bg-sky-500 text-white rounded-full"
          >
            <Save className="w-4 h-4 mr-1" /> {saving ? "Sauvegarde..." : "Sauvegarder"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group rounded-xl border border-sky-800/40 bg-gradient-to-br from-sky-950/30 via-[#0A1428]/60 to-[#0F0407] p-6 backdrop-blur-sm transition-all hover:-translate-y-1 hover:border-sky-600/50 hover:shadow-[0_12px_40px_rgba(96,165,250,0.18)]">
      <Feather className="w-5 h-5 text-sky-300 mb-3" />
      <p className="font-accent text-2xl text-sky-50 leading-snug whitespace-pre-wrap">
        {note.content}
      </p>
      <div className="flex items-end justify-between mt-5">
        <div>
          {note.signed_name && (
            <p className="font-accent text-xl text-sky-300">— {note.signed_name}</p>
          )}
          <p className="text-xs text-sky-200/40 mt-1 tracking-wider uppercase">
            {formatDate(note.created_at)}
          </p>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
          <button
            data-testid={`her-note-edit-btn-${note.id}`}
            onClick={() => setEditing(true)}
            className="w-8 h-8 rounded-full bg-sky-900/40 hover:bg-sky-700/60 text-sky-200 flex items-center justify-center"
            aria-label="Modifier"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            data-testid={`her-note-delete-${note.id}`}
            onClick={() => onDelete(note.id)}
            className="w-8 h-8 rounded-full bg-sky-900/40 hover:bg-rose-700/60 text-sky-200 hover:text-white flex items-center justify-center"
            aria-label="Supprimer"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function HerNotes() {
  const [notes, setNotes] = useState([]);
  const [content, setContent] = useState("");
  const [signedName, setSignedName] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/her_notes");
      setNotes(data);
    } catch (e) {
      console.error("Failed to load her notes", e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const post = async () => {
    if (!content.trim()) {
      toast.error("Écris quelque chose avant d'envoyer");
      return;
    }
    setPosting(true);
    try {
      await api.post("/her_notes", { content, signed_name: signedName });
      toast.success("Mot envoyé ♡", { description: "Il restera ici pour toujours." });
      setContent("");
      setSignedName("");
      load();
    } catch (e) {
      toast.error("Échec de l'envoi");
      console.error(e);
    } finally {
      setPosting(false);
    }
  };

  const update = async (id, body) => {
    try {
      await api.patch(`/her_notes/${id}`, body);
      toast.success("Mot mis à jour");
      load();
    } catch (e) {
      toast.error("Échec");
      console.error(e);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/her_notes/${id}`);
      setNotes((s) => s.filter((x) => x.id !== id));
      toast.success("Mot retiré");
    } catch (e) {
      toast.error("Suppression échouée");
    }
  };

  return (
    <section
      id="ton-espace"
      data-testid="her-notes-section"
      className="px-6 md:px-12 lg:px-24 py-24 lg:py-32 relative"
    >
      <div
        className="absolute inset-0 -z-10 opacity-60"
        style={{
          background:
            "radial-gradient(800px 400px at 50% 10%, rgba(56,189,248,0.10), transparent 60%), radial-gradient(600px 400px at 80% 80%, rgba(37,99,235,0.10), transparent 60%)",
        }}
      />
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <p className="font-accent text-3xl text-sky-300">ton espace à toi</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-sky-50 mt-2">
            Écris-moi
          </h2>
          <p className="text-sky-200/70 mt-3 max-w-xl mx-auto">
            Un coin rien que pour toi. Dis-moi ce que tu ressens, ajoute, modifie, autant que tu veux.
          </p>
        </div>

        <div className="rounded-2xl border border-sky-700/40 bg-gradient-to-br from-sky-950/40 to-[#0F0407]/70 p-6 md:p-8 backdrop-blur-sm mb-12">
          <Label className="text-sky-200/90 font-sans tracking-wide text-xs uppercase">Ton mot</Label>
          <Textarea
            data-testid="her-note-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={5}
            placeholder="Écris tout ce qui te passe par le cœur..."
            className="bg-[#0F0407]/80 border-sky-800/60 text-sky-50 placeholder:text-sky-200/30 mt-2 min-h-[120px]"
          />
          <div className="grid md:grid-cols-[1fr_auto] gap-3 mt-4 items-end">
            <div>
              <Label className="text-sky-200/90 font-sans tracking-wide text-xs uppercase">
                Signature (optionnel)
              </Label>
              <Input
                data-testid="her-note-signed"
                value={signedName}
                onChange={(e) => setSignedName(e.target.value)}
                placeholder="Ton prénom, un surnom..."
                className="bg-[#0F0407]/80 border-sky-800/60 text-sky-50 placeholder:text-sky-200/30 mt-2"
              />
            </div>
            <Button
              data-testid="her-note-submit"
              onClick={post}
              disabled={posting}
              className="bg-sky-600 hover:bg-sky-500 text-white rounded-full h-12 px-7 hover:shadow-[0_0_25px_rgba(56,189,248,0.4)]"
            >
              <Plus className="w-4 h-4 mr-2" />
              {posting ? "Envoi..." : "Envoyer"}
            </Button>
          </div>
        </div>

        {notes.length === 0 ? (
          <div className="rounded-2xl border border-sky-800/40 bg-sky-950/20 p-12 text-center">
            <Feather className="w-10 h-10 text-sky-300 mx-auto" />
            <p className="font-accent text-3xl text-sky-200 mt-4">
              Aucun mot encore... à toi de commencer ♡
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {notes.map((n) => (
              <NoteCard key={n.id} note={n} onEdit={update} onDelete={remove} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
